import { useEffect, useMemo, useState } from 'react';
import { Search, BookOpen, Info, AlertTriangle, Lightbulb } from 'lucide-react';

interface Section {
  id: string;
  title: string;
  content: React.ReactNode;
}

function Callout({ type, children }: { type: 'info' | 'warn' | 'tip'; children: React.ReactNode }) {
  // Design-token palette (teal / warn / ok), and structured like the rest of the
  // app: a 2px top rule over a tinted block — NOT a rounded bordered card. This
  // was the only boxed surface left in an otherwise rule-based UI.
  const cfg = {
    info: { cls: 'border-teal bg-teal-wash', accent: 'text-teal', Icon: Info, label: 'Informație' },
    warn: { cls: 'border-warn bg-warn-wash', accent: 'text-warn', Icon: AlertTriangle, label: 'Atenție' },
    tip:  { cls: 'border-ok bg-ok/10', accent: 'text-ok', Icon: Lightbulb, label: 'Sfat' },
  }[type];
  const { cls, accent, Icon, label } = cfg;
  return (
    <div className={`my-4 flex gap-3 border-t-2 px-4 py-3 text-sm text-ink-2 ${cls}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${accent}`} aria-hidden />
      <div><span className={`mr-1 font-semibold ${accent}`}>{label}:</span>{children}</div>
    </div>
  );
}

/** Splits "3. Procesarea" → { num: "3", text: "Procesarea" } for styled section headers. */
function splitTitle(title: string): { num: string | null; text: string } {
  const m = title.match(/^(\d+)\.\s*(.*)$/);
  return m ? { num: m[1], text: m[2] } : { num: null, text: title };
}

type DotColor = 'ok' | 'warn' | 'bad' | 'mute' | 'teal' | 'ink3';
const DOT_CLASS: Record<DotColor, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  bad: 'bg-bad',
  mute: 'bg-mute',
  teal: 'bg-teal motion-safe:animate-pulse',
  ink3: 'bg-ink-3',
};

/**
 * Small dot + label, matching exactly how status is rendered in the real UI
 * (`MapariListPage`'s `STATUS_META`, `ResultsTable`'s `rowStatusMeta`) —
 * used both inline in prose and in the status legends below, instead of the
 * v1-style colored pill badge this manual used to draw.
 */
function StatusChip({ label, dot }: { label: string; dot: DotColor }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-medium text-ink">
      <span className={`size-2 shrink-0 rounded-full ${DOT_CLASS[dot]}`} aria-hidden />
      {label}
    </span>
  );
}

function StatusList({ items }: { items: Array<{ label: string; dot: DotColor; desc: React.ReactNode }> }) {
  return (
    <div className="space-y-2">
      {items.map(({ label, dot, desc }, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className="shrink-0 pt-0.5"><StatusChip label={label} dot={dot} /></div>
          <p>{desc}</p>
        </div>
      ))}
    </div>
  );
}

const sections: Section[] = [
  {
    id: 'introducere',
    title: '1. Introducere',
    content: (
      <div className="space-y-3 text-sm text-ink-2 leading-relaxed">
        <p>
          <strong>PrivaMed Nomenclator</strong> este un instrument care ajută echipa medicală să
          clasifice automat articolele dintr-un fișier de date (CSV sau Excel) în codul corespunzător
          din baza MEDOC. Sistemul folosește un motor de inteligență artificială pentru a propune
          clasificări, iar operatorul uman confirmă, corectează sau respinge rezultatele. Clasificarea
          rulează pe server, pe o <strong>instanță de procesare</strong> pe care organizația o pornește
          și o plătește din portofelul ei de credite — nu în browser și nu local.
        </p>
        <p>
          Aplicația necesită un cont și o organizație — nu există un mod de utilizare fără
          autentificare (vezi secțiunea următoare). După conectare, navigați între secțiuni din{' '}
          <strong>bara verticală cu iconițe din stânga ecranului</strong> — nu există o bară de
          navigare sus:
        </p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li><strong>Panou</strong> — pagina de start: soldul portofelului de credite și starea instanței de procesare.</li>
          <li><strong>Mapări</strong> — pornirea unei mapări noi, lista mapărilor și pagina fiecărei mapări (progres, revizuire, export).</li>
          <li><strong>Nomenclator</strong> — baza de date cu toate intrările MEDOC ale organizației.</li>
          <li><strong>Echipă</strong> — lista membrilor organizației; proprietarii și administratorii pot invita membri noi, elimina membri și acorda drepturi de editare a nomenclatorului.</li>
          <li><strong>Utilizare</strong> — soldul detaliat, registrul de credite, sesiunile instanței și costurile.</li>
        </ul>
        <p>
          Pentru utilizatorii de tastatură: prima apăsare de tasta Tab pe orice pagină activează un
          link ascuns „Sari la conținut", care duce direct la conținutul paginii, fără a mai trece
          prin toate iconițele barei.
        </p>
        <p>
          După acțiuni precum validarea rândurilor, redenumirea sau ștergerea unei mapări ori
          adăugarea/modificarea/ștergerea unei intrări din nomenclator, apare un mesaj scurt de
          confirmare în colțul din dreapta jos al ecranului, care dispare automat după câteva
          secunde. La eșec, mesajul explică ce nu a reușit.
        </p>
        <p>
          În partea de jos a barei laterale se află butonul de temă (luminos/întunecat), acest ghid,
          <strong> Setări</strong> (profil, schimbarea parolei, preferința de temă și datele
          organizației) și meniul de cont, de unde vă puteți deconecta.
        </p>
        <Callout type="info">
          Dacă soldul organizației scade sub 10% din valoarea lunară, apare un avertisment de
          «sold redus» în partea de sus a paginii, indiferent în ce secțiune vă aflați.
        </Callout>
      </div>
    ),
  },
  {
    id: 'autentificare',
    title: '2. Autentificare și cont',
    content: (
      <div className="space-y-3 text-sm text-ink-2 leading-relaxed">
        <p>
          Accesul în aplicație se face exclusiv prin autentificare cu email și parolă, la pagina de
          conectare. <strong>Nu există autoînregistrare</strong> — un cont nou se creează fie de un
          operator/administrator al organizației, fie prin acceptarea unei invitații.
        </p>
        <h4 className="font-semibold text-ink pt-1">Conectare</h4>
        <p>
          Introduceți emailul și parola contului dumneavoastră și apăsați <strong>Autentificare</strong>.
          Dacă datele sunt greșite, mesajul afișat este generic („Email sau parolă incorecte."), fără a
          preciza care dintre cele două e greșită, din motive de securitate. Pentru o parolă uitată,
          contactați operatorul — aplicația nu are momentan un flux propriu de resetare a parolei.
        </p>
        <h4 className="font-semibold text-ink pt-1">Activarea unui cont prin invitație</h4>
        <p>
          Dacă ați primit un link de invitație, deschideți-l, completați numele și o parolă (cu
          confirmare) și apăsați <strong>Activează contul</strong>. Contul este creat direct în
          organizația care v-a invitat, iar dumneavoastră sunteți autentificat automat, fără un pas
          separat de conectare.
        </p>
        <Callout type="info">
          Fiecare cont aparține unei singure organizații. Datele, mapările și nomenclatorul unei
          organizații nu sunt niciodată vizibile altei organizații.
        </Callout>
        <h4 className="font-semibold text-ink pt-1">Deconectare și temă</h4>
        <p>
          Din meniul de cont (iconița din partea de jos a barei laterale) alegeți{' '}
          <strong>Deconectare</strong> (aceeași acțiune este disponibilă și din pagina Setări).
          Butonul Lună/Soare de deasupra meniului de cont schimbă rapid tema aplicației între
          luminos și întunecat; pentru a urmări automat tema sistemului de operare, alegeți
          „Sistem" din pagina <strong>Setări → Preferințe</strong>. Alegerea făcută se păstrează la
          următoarea vizită.
        </p>
      </div>
    ),
  },
  {
    id: 'mapari',
    title: '3. Mapări',
    content: (
      <div className="space-y-3 text-sm text-ink-2 leading-relaxed">
        <p>
          Fiecare sesiune de procesare este salvată ca o <strong>mapare</strong> în baza de date.
          Secțiunea <strong>Mapări</strong> afișează toate mapările înregistrate, grupate pe zile,
          cu un filtru după status și o căutare după numele fișierului.
        </p>
        <h4 className="font-semibold text-ink pt-1">Pornirea unei mapări noi</h4>
        <ol className="list-decimal list-inside space-y-1 pl-2">
          <li>Din secțiunea <strong>Mapări</strong>, apăsați <strong>Mapare nouă</strong>.</li>
          <li>Glisați fișierul (CSV, XLS sau XLSX, până la 5MB) pe zona de încărcare sau apăsați-o pentru a-l alege.</li>
          <li>
            Confirmați <strong>maparea coloanelor</strong> — care coloană este codul partenerului, care
            este denumirea și, opțional, care este valoarea/tariful. Selecția este precompletată automat
            pe baza antetului fișierului; o previzualizare cu primele 5 rânduri vă ajută să verificați.
          </li>
          <li>
            Sistemul afișează un <strong>rezumat al mapării</strong> — câte rânduri vor fi clasificate,
            fișierul ales, timpul și costul estimat și soldul disponibil (costul și soldul sunt vizibile
            doar pentru administratori) — plus ce se întâmplă la pornire. Tot aici puteți completa{' '}
            <strong>Nume mapare</strong>: numele sub care maparea apare în listă. Dacă îl lăsați gol,
            se folosește numele fișierului încărcat, ca până acum; numele poate fi oricând schimbat
            ulterior din lista de mapări. Apăsați <strong>Începe maparea</strong>; dacă nu există o instanță activă, una este pornită automat înainte de
            procesare. Pornirea unei instanțe este rezervată administratorilor: dacă sunteți membru
            și nu există o instanță activă, cereți unui administrator să pornească una.
          </li>
        </ol>
        <Callout type="warn">
          Dacă soldul organizației este insuficient pentru a porni o instanță, sistemul refuză
          pornirea și afișează un mesaj — contactați administratorul organizației pentru realimentare.
        </Callout>
        <Callout type="info">
          Încărcarea fișierului și maparea coloanelor funcționează în orice moment, indiferent dacă
          instanța este deja activă sau tocmai pornește. Cât timp instanța se pregătește, maparea nouă
          este creată imediat și rămâne <strong>în coadă</strong> — procesarea ei începe automat, fără
          nicio acțiune suplimentară, de îndată ce instanța este gata.
        </Callout>
        <h4 className="font-semibold text-ink pt-2">Statusurile unei mapări</h4>
        <StatusList
          items={[
            { label: 'În coadă', dot: 'warn', desc: 'Mapare în așteptare să fie (re)preluată de sistem — inclusiv una neîncepută sau una întreruptă (de ex. de un repornire de server) care își va relua automat cursul.' },
            { label: 'În curs', dot: 'teal', desc: 'Procesarea este activă în acest moment.' },
            { label: 'În validare', dot: 'warn', desc: 'Toate rândurile au fost procesate, dar maparea încă nu a fost blocată pentru editare — rândurile pot fi în continuare revizuite și corectate.' },
            { label: 'Finalizat', dot: 'ok', desc: 'Toate rândurile au fost procesate cu succes, iar maparea a fost blocată pentru editare (vezi „Blocarea unei mapări pentru editare" mai jos).' },
            { label: 'Anulat', dot: 'mute', desc: 'Maparea a fost oprită manual înainte de finalizare.' },
            { label: 'Eșuat', dot: 'bad', desc: 'O eroare a oprit procesarea — inclusiv atunci când instanța de procesare nu a putut porni la timp. Puteți reporni maparea cu butonul Repornire.' },
          ]}
        />
        <h4 className="font-semibold text-ink pt-2">Statusurile unui rând</h4>
        <p>
          În plus față de gruparea din <em>Lista de rezultate</em> (mai jos), fiecare rând poartă o
          etichetă de status mai precisă:
        </p>
        <StatusList
          items={[
            { label: 'Eroare', dot: 'bad', desc: 'Clasificarea acestui rând a eșuat dintr-o eroare tehnică. Poate fi retrimisă cu «Reîncearcă».' },
            { label: 'În așteptare', dot: 'mute', desc: 'Rândul nu a fost procesat încă — apare doar cât timp maparea este în coadă sau în curs.' },
            { label: 'Confirmat', dot: 'ok', desc: 'Operatorul a confirmat sugestia principală a modelului ca fiind corectă.' },
            { label: 'Rezolvat manual', dot: 'ok', desc: 'Operatorul a ales o variantă alternativă propusă de model sau un cod căutat manual în nomenclator.' },
            { label: 'Nerezolvat', dot: 'mute', desc: 'Operatorul a marcat rândul ca neavând niciun cod MEDOC corespunzător.' },
            { label: 'Rezolvat', dot: 'ok', desc: 'Modelul a identificat automat o clasificare cu încredere suficientă, fără intervenție.' },
            { label: 'Ambiguu', dot: 'warn', desc: 'Modelul a găsit mai multe coduri MEDOC posibil potrivite — variantele apar în panoul de revizuire.' },
            { label: 'Revizuire', dot: 'warn', desc: 'Modelul propune o clasificare, dar nu este suficient de sigur — confirmați sau corectați.' },
            { label: 'Fără cod final', dot: 'mute', desc: 'Modelul nu a găsit niciun cod MEDOC apropiat pentru acest articol.' },
            { label: 'Încredere scăzută', dot: 'warn', desc: 'A fost găsit un candidat, dar sub pragul de similaritate pentru o clasificare sigură — recomandată maparea manuală.' },
            { label: 'Clasificat', dot: 'ok', desc: 'Rândul are o clasificare finală, fără a se încadra în categoriile de mai sus (de exemplu, un rând editat direct).' },
          ]}
        />
        <h4 className="font-semibold text-ink pt-2">Costul unei mapări</h4>
        <p>
          Odată ce o mapare s-a încheiat, coloana <strong>Cost</strong> din lista de mapări și
          linia de cost afișată pe pagina proprie a mapării arată <strong>costul de calcul</strong> al
          acelei mapări: <em>durata rulării</em> (de la începerea până la finalizarea procesării)
          înmulțită cu <strong>tariful pe oră</strong> al instanței organizației dumneavoastră.
          Același calcul, pentru toate mapările recente, este centralizat în tabelul{' '}
          <strong>„Utilizare → Cost per mapare"</strong>.
        </p>
        <Callout type="info">
          Acesta este un cost de <em>atribuire</em> a timpului de calcul unei anumite mapări, nu suma
          exactă reținută din portofel. Suma reținută efectiv se facturează pe <em>sesiune de
          instanță</em> (vezi „Instanțe de procesare"), cu un <strong>minim de 1 oră</strong> per
          sesiune — dacă mai multe mapări scurte rulează în aceeași sesiune, costul lor însumat poate
          fi mai mic decât suma reținută efectiv pentru acea sesiune.
        </Callout>
        <h4 className="font-semibold text-ink pt-2">Acțiuni disponibile</h4>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li><strong>Deschide</strong> — deschide maparea la adresa proprie (/mapari/&lt;id&gt;).</li>
          <li><strong>Reia</strong> — pentru mapările în curs sau în coadă, deschide maparea pentru a urmări progresul (procesarea continuă automat pe server).</li>
          <li><strong>Anulează</strong> — oprește procesarea unei mapări în curs sau în coadă; necesită confirmare directă în rând.</li>
          <li><strong>Repornire</strong> — disponibil doar pentru mapări eșuate; le repune la coadă pentru reluare automată.</li>
          <li><strong>Redenumire</strong> — apăsați pictograma creion de lângă numele fișierului; câmpul de editare are o etichetă dedicată pentru cititoarele de ecran. (Apăsarea pe numele fișierului <em>deschide</em> maparea.)</li>
          <li><strong>Blochează editarea / Redeschide</strong> — vezi secțiunea dedicată mai jos.</li>
          <li><strong>Șterge</strong> — disponibil pentru mapările finalizate (neblocate) sau anulate; o mapare <strong>blocată pentru editare</strong> nu poate fi ștearsă până nu este redeschisă. Necesită confirmare directă în rând.</li>
        </ul>
        <Callout type="warn">
          Ștergerea unei mapări este permanentă și elimină toate rândurile asociate din baza de date.
        </Callout>
        <h4 className="font-semibold text-ink pt-2">Blocarea unei mapări pentru editare</h4>
        <p>
          Când o mapare este gata — toate rândurile au fost verificate și rezultatele sunt corecte —
          apăsați <strong>Blochează editarea</strong> (disponibil atât din lista de mapări, cât și
          de pe pagina proprie a mapării, pentru orice mapare finalizată care are rezultate).
          Maparea primește eticheta <StatusChip label="Blocată pentru editare" dot="ink3" /> și intră în regim{' '}
          <strong>doar-citire</strong>: rândurile ei nu mai pot fi editate, validate (individual sau
          în masă) sau reîncercate, pentru a garanta că rezultatele exportate rămân exact cele
          confirmate ca finale.
        </p>
        <p>
          Pentru a face modificări ulterioare, apăsați <strong>Redeschide</strong> — maparea revine
          la starea editabilă, eticheta „Blocată pentru editare" dispare, iar toate acțiunile (editare, validare,
          reîncercare) devin din nou disponibile.
        </p>
        <h4 className="font-semibold text-ink pt-2">Urmărirea unei mapări în curs</h4>
        <p>
          Cât timp o mapare este <strong>în coadă</strong> sau <strong>în curs</strong>, pagina ei
          afișează o bară de progres cu numărul de rânduri procesate din total și rândul pe care
          sistemul îl clasifică chiar acum (codul și denumirea lui) — actualizate automat la
          fiecare verificare periodică a stării mapării. Timpul mediu de procesare pe rând și
          timpul scurs de la pornire se recalculează în plus, în fiecare secundă. De aici puteți
          apăsa direct <strong>Anulează</strong> pentru a opri maparea. Pentru cititoarele de ecran,
          progresul este anunțat din 10 în 10 procente, plus un mesaj când maparea s-a încheiat — nu
          la fiecare rând în parte, ca să rămână urmăribil și pe fișiere de peste 1.000 de rânduri.
        </p>
        <Callout type="info">
          Dacă maparea este încă în coadă pentru că instanța de procesare tocmai pornește, bara de
          progres este înlocuită temporar cu mesajul <em>Se pornește instanța… maparea începe automat
          (2–5 min)</em>. Bara de progres obișnuită reapare de îndată ce instanța este gata și
          procesarea rândurilor începe efectiv.
        </Callout>
        <h4 className="font-semibold text-ink pt-2">Vizualizarea rezultatelor: Validare sau Toate rândurile</h4>
        <p>
          Deschizând o mapare, pagina se deschide implicit în modul <strong>Validare</strong> —
          asistentul ghidat descris la finalul acestei secțiuni. După ce procesarea s-a încheiat și
          maparea are cel puțin un rând de afișat, apare comutatorul <strong>Toate rândurile |
          Validare</strong>, sub titlul mapării, care trece la vizualizarea alternativă,{' '}
          <strong>Toate rândurile</strong> — un tabel unic cu toate rândurile mapării, filtrabil și
          căutabil, descris în continuare.
        </p>
        <h4 className="font-semibold text-ink pt-2">Rezumatul și filtrarea rezultatelor</h4>
        <p>
          Deasupra tabelului, un rezumat arată într-o singură propoziție câte rânduri au fost{' '}
          <strong>validate</strong> din câte au fost <strong>clasificate</strong> (de exemplu „128
          validate din 150 clasificate"), o bară colorată cu distribuția pe cele patru categorii de
          mai jos și butoane cu numărul de rânduri din fiecare: <strong>Rezolvate</strong>,{' '}
          <strong>De verificat</strong>, <strong>Fără cod final</strong> și <strong>Erori</strong>.
          Apăsând pe oricare dintre aceste butoane, tabelul se restrânge la categoria aleasă;
          În plus, rândurile pot fi căutate după codul sau denumirea din fișierul original, filtrate
          după nivelul de încredere al clasificării (ridicată/medie/scăzută) și sortate după cod,
          denumire, cod MEDOC sau încredere.
        </p>
        <h4 className="font-semibold text-ink pt-2">Lista de rezultate</h4>
        <p>
          În vizualizarea <strong>Toate rândurile</strong>, rândurile filtrate sunt afișate grupate în
          patru secțiuni, în ordine: <strong>De verificat</strong>{' '}
          (rânduri ambigue, fără potrivire clară sau care necesită revizuire), <strong>Fără cod final</strong>{' '}
          (rânduri închise fără o corespondență MEDOC), <strong>Rezolvate</strong> (rânduri cu o clasificare
          finală — restrânsă implicit, cu un buton „Afișează cele … rezolvate" pentru a le desface) și{' '}
          <strong>Erori</strong> (rânduri la care clasificarea a eșuat). O secțiune fără rezultate nu este
          afișată. Pentru fiecare rând, o săgeată dedicată deschide/închide panoul de detaliu al acelui rând.
        </p>
        <h4 className="font-semibold text-ink pt-2">Revizuirea și corectarea rândurilor</h4>
        <p>
          Pentru orice rând din secțiunile <strong>De verificat</strong>, <strong>Fără cod final</strong>{' '}
          sau <strong>Erori</strong>, apăsați săgeata <em>Revizuiește</em> din capătul rândului pentru a
          deschide panoul de corectare. Un rând deja <strong>Rezolvat</strong> poate fi redeschis pentru
          corectare cu linkul <em>Corectează</em>, aflat lângă motivația afișată.
        </p>
        <p>
          Panoul de corectare poate fi controlat integral de la tastatură: la deschidere, focusul sare
          automat în interiorul panoului; tasta Tab rămâne în interiorul lui cât timp e deschis; Esc îl
          închide și revine la săgeata Revizuiește.
        </p>
        <p>În panoul de corectare aveți la dispoziție:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li><strong>Confirmă</strong> — acceptă sugestia principală a modelului drept clasificare finală.</li>
          <li>
            <strong>Alege această variantă</strong> — pentru fiecare variantă alternativă propusă de
            model, dacă aceasta se potrivește mai bine decât sugestia principală.
          </li>
          <li>
            <strong>Caută în nomenclator</strong> — un câmp de căutare (după cod MEDOC, cod BD MEDOC
            sau denumire) prin care alegeți manual codul corect din baza de date, atunci când niciuna
            dintre variantele propuse de sistem nu este potrivită. <strong>Alegerea din listă se aplică
            imediat</strong> — nu mai apăsați nimic după ea; codul ales este confirmat pe loc, sub
            câmpul de căutare.
          </li>
          <li><strong>Fără corespondent</strong> — marchează rândul ca neavând niciun cod MEDOC corespunzător.</li>
        </ul>
        <Callout type="info">
          După oricare dintre aceste acțiuni, rândul se regrupează automat — de exemplu, un rând
          corectat din „De verificat" trece imediat în „Rezolvate", iar unul marcat „Fără corespondent"
          trece în secțiunea „Fără cod final".
        </Callout>
        <h4 className="font-semibold text-ink pt-2">Validarea în masă</h4>
        <p>
          Pentru rândurile din secțiunea <strong>De verificat</strong>, puteți bifa individual
          rândurile dorite sau apăsa linkul <em>Selectează tot</em> din capul secțiunii pentru a le
          bifa pe toate deodată. De îndată ce este selectat cel puțin un rând, apare o bară de acțiuni
          cu:
        </p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>
            <strong>Validează selectate</strong> — confirmă sugestia principală a modelului pentru
            toate rândurile bifate, dintr-o singură apăsare.
          </li>
          <li><strong>Fără corespondent</strong> — marchează toate rândurile bifate ca neavând corespondent.</li>
        </ul>
        <p>Linkul <em>Anulează selecția</em> golește bifele, fără a face nicio modificare. Rezultatul
          validării în masă (câte rânduri au fost validate, sau mesajul de eroare dacă acțiunea a
          eșuat) este anunțat și pentru cititoarele de ecran.</p>
        <h4 className="font-semibold text-ink pt-2">Reîncercarea rândurilor eșuate</h4>
        <p>
          Rândurile din secțiunea <strong>Erori</strong> pot fi retrimise spre clasificare. Folosiți
          iconița <em>Reîncearcă</em> de lângă un rând anume, sau linkul <em>Reîncearcă toate</em> din
          capul secțiunii Erori pentru a le retrimite pe toate deodată.
        </p>
        <Callout type="warn">
          Reîncercarea necesită o <strong>instanță de procesare activă</strong>. Dacă nu există niciuna
          pornită, apare butonul <strong>Pornește instanța</strong> — pornirea consumă credite din
          portofelul organizației (vezi secțiunea „Instanțe de procesare").
        </Callout>
        <h4 className="font-semibold text-ink pt-2">Asistentul de validare</h4>
        <p>
          <strong>Validare</strong> este modul implicit de revizuire (vezi mai sus) — un asistent care
          parcurge rândurile mapării pas cu pas, grupate pe categorii, în loc să le arate pe toate
          deodată ca în <strong>Toate rândurile</strong>. Comutatorul dintre cele două vizualizări
          apare doar după ce procesarea s-a încheiat și maparea are cel puțin un rând de afișat.
        </p>
        <p>
          În stânga asistentului apar șapte pași, în ordine: <strong>Confirmate / Rezolvate</strong>,{' '}
          <strong>Ambigue</strong>, <strong>Revizuire</strong>, <strong>Încredere scăzută</strong>,{' '}
          <strong>Fără corespondent</strong>, <strong>Erori</strong> și, la sfârșit,{' '}
          <strong>Sumar</strong>. Fiecare pas arată câte rânduri mai are de rezolvat; când nu mai
          rămâne niciunul, numărul este înlocuit cu un bifat verde. La deschiderea asistentului,
          sistemul sare direct la primul pas care mai are ceva de făcut.
        </p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>
            <strong>Confirmate / Rezolvate</strong> — rândurile clasificate automat cu încredere
            ridicată. Fiecare rând arată denumirea de la partener și denumirea MEDOC una lângă alta,
            despărțite doar de săgeată, ca să le puteți compara dintr-o privire. Fiecare rând poate fi
            editat direct sau marcat incorect cu iconițele dedicate; selectați rândurile dorite
            (implicit sunt bifate toate) și apăsați <strong>Validează N selectate</strong> pentru a le
            confirma dintr-o singură apăsare.
          </li>
          <li>
            <strong>Fără corespondent</strong> — funcționează la fel ca pasul de mai sus, dar butonul{' '}
            <strong>Validează ca fără corespondent (N)</strong> confirmă rândurile selectate ca
            neavând niciun cod MEDOC corespunzător.
          </li>
          <li>
            <strong>Erori</strong> — rândurile la care clasificarea a eșuat. Nu au bifă de selecție;
            butonul <strong>Reclasifică toate</strong> retrimite spre clasificare, pe rând, toate
            rândurile pasului.
          </li>
          <li>
            <strong>Ambigue</strong> — rândurile pentru care modelul a găsit deodată mai multe coduri
            MEDOC posibil potrivite (de exemplu un pachet de analize care ar trebui, de fapt, mapat pe
            mai multe linii separate). Fiecare candidat are propria bifă, bifată implicit:{' '}
            <strong>debifați candidatul care nu se potrivește</strong> și el nu va mai deveni rând
            separat. <strong>Generează separat</strong> înlocuiește rândul ambiguu cu câte un rând nou
            pentru fiecare candidat rămas bifat. Rândurile noi ajung la pasul{' '}
            <strong>Confirmate / Rezolvate</strong>, fiecare cu codul lui MEDOC deja atribuit — le
            validați pe toate dintr-o singură apăsare, nu una câte una. (Rândul original dispare din
            tabel și din numărători — este înlocuit de cele noi; operația rămâne înregistrată în baza
            de date, nu ca rând separat în tabel.) Dacă rămâne bifat un
            singur candidat, butonul devine <strong>Confirmă varianta</strong> — nu mai este o
            împărțire, ci pur și simplu alegerea acelui cod. <strong>Marchează nerezolvat</strong>
            rămâne disponibil dacă niciun candidat nu se potrivește.
          </li>
          <li>
            <strong>Revizuire</strong> și <strong>Încredere scăzută</strong> — aceleași rânduri de tip
            „necesită revizuire" și „încredere scăzută" descrise mai sus, afișate unul după altul.
            Fiecare rând începe cu cele trei decizii — <strong>Confirmă</strong>,{' '}
            <strong>Fără corespondent</strong>, <strong>Marchează incorect</strong> — astfel încât să
            le puteți apăsa fără să derulați. Dedesubt, ce vine de la partener și ce propune modelul
            apar în două coloane alăturate, etichetate <strong>De la partener</strong> și{' '}
            <strong>Propunere AI</strong>, ca să nu le confundați. Restul opțiunilor (variante
            alternative, comparația între modele, căutarea în nomenclator și editarea manuală) stau
            strânse sub <strong>Altă variantă</strong> și se desfac doar la nevoie.
          </li>
        </ul>
        <p>
          La toți pașii cu listă (<strong>Confirmate / Rezolvate</strong>, <strong>Fără corespondent</strong>
          și <strong>Erori</strong>), ca și la <strong>Ambigue</strong>, <strong>Revizuire</strong> și{' '}
          <strong>Încredere scăzută</strong>, bara de acțiune rămâne fixată în partea de jos a
          ecranului, vizibilă indiferent cât de mult derulați lista. La <strong>Ambigue</strong> ea
          poartă numele <strong>Generează separat pentru N rânduri</strong> și aplică fiecărui rând
          bifat exact ce ar fi făcut butonul lui propriu, cu bifele de candidați alese de
          dumneavoastră. La pașii cu selecție (<strong>Confirmate / Rezolvate</strong>
          și <strong>Fără corespondent</strong>), dacă selecția depășește 25 de rânduri, prima apăsare a butonului nu
          validează imediat — bara cere confirmarea „Validezi N rânduri?", iar validarea are loc abia
          la a doua apăsare, pe <strong>Da, validează</strong>; <strong>Anulează</strong> revine la
          starea inițială, fără nicio modificare.
        </p>
        <Callout type="warn">
          Spre deosebire de reîncercarea din tabel, <strong>Reclasifică toate</strong> din asistent nu
          afișează avertismentul și butonul <strong>Pornește instanța</strong> atunci când nu există
          nicio instanță de procesare activă — rândurile pur și simplu rămân neschimbate, fără nicio
          explicație pe ecran. Dacă nu sunteți sigur că aveți o instanță pornită, folosiți reîncercarea
          din vizualizarea <strong>Toate rândurile</strong> (secțiunea „Reîncercarea rândurilor eșuate" de mai
          sus), care vă ghidează să porniți instanța la nevoie.
        </Callout>
        <p>
          Ultimul pas, <strong>Sumar</strong>, arată câte rânduri au fost validate din câte au fost
          clasificate. Dacă unele rânduri au fost clasificate automat de model, dar nu figurează
          încă drept validate, apare butonul <strong>Validează tot ce a rămas</strong>, care le
          confirmă pe toate deodată (astfel numărul de validate ajunge la numărul de clasificate).
          Dacă mai rămân cazuri nerezolvate, apare o listă cu numărul de rânduri rămase pe fiecare
          pas neterminat și un buton <strong>Continuă cu ...</strong> către primul pas cu rânduri de
          rezolvat. Odată ce totul este validat, butonul <strong>Finalizează maparea</strong> închide
          și blochează maparea direct din Sumar. Linkul <strong>Înapoi la tabel</strong> readuce la
          vizualizarea Toate rândurile a aceleiași mapări.
        </p>
        <Callout type="info">
          Pe o mapare <strong>blocată pentru editare</strong>, asistentul rămâne disponibil pentru
          consultare, dar toate acțiunile lui (bifare, editare, validare, reclasificare, generare
          separată) sunt dezactivate — la fel ca în tabel.
        </Callout>
        <h4 className="font-semibold text-ink pt-2">Exportul rezultatelor</h4>
        <p>
          Butonul <strong>Export</strong>, din capul paginii mapării (vizibil cât timp maparea nu este
          în curs de procesare și are cel puțin un rând), deschide o pagină dedicată de export, cu
          setările în stânga și o previzualizare live a rezultatului în dreapta.
        </p>
        <p>În pagina de export puteți alege:</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>
            <strong>Rânduri</strong> — <strong>Toate</strong> (toate rândurile mapării, mai puțin
            rândurile-părinte înlocuite prin „Generează separat") sau <strong>Pe grupe</strong>, care
            afișează cele patru categorii din lista de rezultate (De verificat, Fără cod final,
            Rezolvate, Erori), fiecare cu bifă proprie și numărul ei de rânduri. Pe grupe, fișierul
            este scris <strong>bloc cu bloc, în ordinea din listă</strong>: prindeți o grupă de mâner
            și trageți-o, sau folosiți săgețile ↑ / ↓ de pe rândul ei. Debifați o grupă și rândurile
            ei nu mai ajung în fișier.
          </li>
          <li>
            <strong>Coloane</strong> — coloanele incluse apar într-o listă, <strong>în chiar ordinea
            în care sunt scrise în fișier</strong>. Le rearanjați trăgându-le de mâner sau cu săgețile
            ↑ / ↓ de pe fiecare rând. Debifând una, o scoateți din fișier; coloanele rămase
            disponibile sunt listate dedesubt, iar bifarea uneia o adaugă <em>la sfârșit</em>, ca să nu
            vă strice aranjamentul. Presetările <strong>Basic</strong> (coloanele esențiale: cod,
            denumire, preț, cod MEDOC/BD, denumire MEDOC) și <strong>Complet</strong> (toate coloanele
            MEDOC, statusul de workflow și toate coloanele originale din fișierul încărcat) refac
            dintr-o apăsare atât selecția, cât și ordinea lor.
          </li>
          <li>
            <strong>Ordonare</strong> — alegeți coloana după care se ordonează <em>rândurile</em> și
            sensul (crescător / descrescător). Este o ordonare diferită de cea a coloanelor: aici
            contează <em>valoarea</em> din celulă. Dacă exportați pe grupe, ordonarea se aplică în
            interiorul fiecărei grupe, nu peste tot fișierul. Celulele goale ajung întotdeauna la
            sfârșit, în ambele sensuri.
          </li>
          <li>
            <strong>Profiluri salvate</strong> — salvează combinația curentă de coloane{' '}
            <em>și ordinea lor</em>, împreună cu formatul, sub un nume ales, pentru a le reaplica
            rapid la un export viitor; profilurile pot fi și șterse.
          </li>
        </ul>
        <Callout type="info">
          Rândurile din categoria <strong>Fără cod final</strong> se exportă{' '}
          <strong>fără nicio intrare MEDOC</strong>: coloanele „COD MEDOC", „Cod BD MEDOC" și
          „Denumire MEDOC" rămân goale, pentru că acele rânduri nu au primit un cod. Propunerea
          respinsă a modelului rămâne vizibilă doar în coloanele numite explicit „Cod MEDOC
          provizoriu" și „Denumire MEDOC provizorie", disponibile în presetarea <strong>Complet</strong>.
        </Callout>
        <p>
          Numele fișierului se editează direct în capul paginii; formatul se alege între{' '}
          <strong>CSV</strong> și <strong>XLSX</strong>. Butonul <strong>Descarcă</strong> pornește
          descărcarea, iar <strong>Copiază</strong> pune același conținut (separat prin tab) direct în
          clipboard, gata de lipit într-un tabel. Previzualizarea din dreapta arată exact ce se va
          exporta — rândurile din domeniul ales, cu coloanele bifate, în ordinea aleasă.
        </p>
      </div>
    ),
  },
  {
    id: 'nomenclator',
    title: '4. Nomenclator',
    content: (
      <div className="space-y-3 text-sm text-ink-2 leading-relaxed">
        <p>
          Secțiunea <strong>Nomenclator</strong> afișează toate intrările MEDOC salvate în baza de
          date a organizației — codul BD MEDOC, codul MEDOC și denumirea. Un contor în partea de
          sus a paginii arată numărul total de înregistrări.
        </p>
        <p>
          Puteți căuta liber după cod sau denumire și, opțional, restrânge rezultatele exact după{' '}
          <strong>Cod BD MEDOC</strong> sau <strong>Cod MEDOC</strong>; căutarea se aplică la
          apăsarea <strong>Caută</strong> (sau Enter), nu la fiecare literă tastată.
          <strong> Resetează filtrele</strong> golește căutarea curentă. Rezultatele sunt paginate,
          cu alegerea a 25, 50 sau 100 de înregistrări pe pagină și navigare înainte/înapoi.
        </p>
        <p>
          Utilizatorii cu drept de editare a nomenclatorului (rolul de proprietar sau administrator
          al organizației, ori permisiunea dedicată) au în plus la dispoziție:
        </p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>
            <strong>Adaugă înregistrare</strong> — deschide un formular direct în pagină (fără
            fereastră separată) cu cele trei câmpuri, toate obligatorii.
          </li>
          <li>
            <strong>Editează</strong> — pe rândul dorit, transformă cele trei câmpuri în casete
            editabile direct în tabel; <strong>Salvează</strong> aplică modificarea,{' '}
            <strong>Anulează</strong> (sau tasta Esc) o abandonează fără să salveze.
          </li>
          <li>
            <strong>Șterge</strong> — necesită o confirmare directă în rând („Sigur? Da, șterge")
            înainte de a elimina definitiv intrarea.
          </li>
          <li>
            <strong>Importă</strong> — încarcă un fișier <strong>CSV, XLS sau XLSX</strong> (prima
            linie trebuie să fie antetul) pentru a actualiza nomenclatorul în masă. După încărcare
            alegeți, din coloanele fișierului, care coloană conține <strong>Cod BD</strong>,{' '}
            <strong>COD MEDOC</strong> și <strong>Denumire</strong> (aplicația încearcă să le
            potrivească automat după numele antetului) și vedeți o previzualizare a primelor rânduri.
            Rândurile cu un câmp obligatoriu gol sunt ignorate și numărate separat.
          </li>
        </ul>
        <p>
          La import alegeți una dintre cele două <strong>operații</strong>:
        </p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>
            <strong>Actualizează</strong> — adaugă rândurile noi și le actualizează pe cele
            existente (după COD MEDOC); intrările care nu apar în fișier rămân neatinse.
          </li>
          <li>
            <strong>Înlocuiește</strong> — <strong>șterge toate</strong> intrările existente ale
            organizației, apoi încarcă fișierul. Fișierul devine întregul nomenclator.
          </li>
        </ul>
        <p>
          La final se afișează un rezumat: câte intrări au fost <strong>adăugate</strong>,{' '}
          <strong>actualizate</strong> și (la înlocuire) <strong>șterse</strong>.
        </p>
        <Callout type="warn">
          <strong>Înlocuiește</strong> este ireversibil — șterge tot nomenclatorul organizației
          înainte de a încărca fișierul. De aceea cere o bifă de confirmare explicită înainte de a
          porni. Folosiți <strong>Actualizează</strong> dacă vreți doar să completați sau să
          corectați intrări, păstrându-le pe celelalte.
        </Callout>
        <Callout type="warn">
          O încercare de a adăuga sau edita o intrare cu un cod deja existent este respinsă cu
          mesajul „Există deja o înregistrare identică."
        </Callout>
        <p>
          Utilizatorii fără acest drept văd lista în mod <strong>doar-citire</strong> — fără
          formularul de adăugare și fără acțiunile de editare sau ștergere pe rânduri.
        </p>
        <p>
          Butonul <strong>Exportă</strong>, disponibil tuturor utilizatorilor, descarcă în format
          CSV <strong>întreaga listă filtrată</strong> (conform căutării și filtrelor active), din
          toate paginile — nu doar pagina afișată.
        </p>
      </div>
    ),
  },
  {
    id: 'intrebari',
    title: '5. Întrebări frecvente',
    content: (
      <div className="space-y-4 text-sm text-ink-2 leading-relaxed">
        {[
          {
            q: 'Pot folosi aplicația fără cont?',
            a: 'Nu. Fiecare acțiune — inclusiv clasificarea — necesită o sesiune autentificată și rulează pe server, în contul organizației dumneavoastră. Dacă nu aveți cont, cereți unui administrator să vă invite.',
          },
          {
            q: 'Cum îmi creez un cont?',
            a: 'Nu există autoînregistrare. Un administrator al organizației vă trimite un link de invitație; deschideți-l, completați numele și o parolă, iar contul este activat imediat.',
          },
          {
            q: 'Ce fac dacă o mapare s-a oprit la jumătate?',
            a: 'Maparea a fost salvată automat. Mergeți la secțiunea Mapări, găsiți-o (status „În coadă" sau „În curs") și apăsați „Reia" pentru a urmări progresul — procesarea continuă automat pe server chiar dacă închideți browserul.',
          },
          {
            q: 'Pot modifica manual o clasificare după ce a fost confirmată?',
            a: 'Da, cât timp maparea nu este finalizată. Deschideți rândul din pagina mapării (săgeata „Revizuiește" sau „Corectează", lângă motivație) și folosiți „Caută în nomenclator" pentru a alege manual alt cod.',
          },
          {
            q: 'De ce unele rânduri au încredere mică?',
            a: 'Nivelul de încredere reflectă cât de bine se potrivește denumirea din fișier cu intrările din baza MEDOC. O denumire prea scurtă, prescurtată sau cu greșeli de scriere poate duce la o încredere mai mică — verificați manual aceste rânduri din secțiunea „De verificat".',
          },
          {
            q: 'Ce înseamnă statusul Ambiguu?',
            a: 'Sistemul a identificat mai multe coduri MEDOC posibil potrivite pentru același articol. Deschideți rândul din „De verificat" și alegeți varianta corectă, sau marcați-l „Fără corespondent" dacă niciuna nu se potrivește.',
          },
          {
            q: 'Pot exporta doar o parte din rezultate?',
            a: 'Da. În pagina de export (butonul „Export" din capul mapării), alegeți „Pe grupe" la secțiunea Rânduri și bifați doar categoriile dorite (De verificat, Fără cod final, Rezolvate, Erori) — sau restrângeți coloanele exportate din secțiunea Coloane. Exportul din secțiunea Nomenclator descarcă întreaga listă filtrată (toate paginile care corespund căutării și filtrelor).',
          },
          {
            q: 'Ce se întâmplă dacă închid aplicația în timpul procesării?',
            a: 'Procesarea continuă pe server, chiar dacă vă deconectați. Redeschideți aplicația, mergeți la Mapări și apăsați „Reia" pentru a vedea progresul sau rezultatele.',
          },
          {
            q: 'De ce nu pot reîncerca rândurile cu eroare?',
            a: 'Reîncercarea are nevoie de o instanță de procesare activă pentru organizația dvs. Dacă nu există niciuna pornită, apare butonul „Pornește instanța" direct în bara de reîncercare — pornirea consumă credite din portofel.',
          },
          {
            q: 'De ce nu mai pot edita rândurile unei mapări?',
            a: 'Maparea a fost blocată pentru editare — un regim protejat, gândit să păstreze rezultatele exportate neschimbate. Apăsați „Redeschide" (din lista de mapări sau din pagina mapării) pentru a relua editarea.',
          },
        ].map(({ q, a }, i) => (
          <div key={i} className="border-b border-rule-soft pb-4 last:border-0 last:pb-0">
            <p className="font-semibold text-ink mb-1">{q}</p>
            <p>{a}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'portofel-credite',
    title: 'Portofel de credite',
    content: (
      <div className="space-y-3 text-sm text-ink-2 leading-relaxed">
        <p>Fiecare organizație are un <strong>portofel de credite</strong> exprimat în euro. În modul pilot, portofelul pornește de la 500 € și se reînnoiește lunar la 500 €.</p>
        <p>Soldul curent apare pe <strong>Panou</strong> și în detaliu în <strong>Utilizare</strong>. Când soldul scade sub 10% din valoarea lunară, apare un avertisment de <em>sold redus</em> în partea de sus a ecranului.</p>
        <p>Toate mișcările (alimentări și consum) sunt înregistrate în <strong>Registrul de credite</strong> din pagina Utilizare.</p>
      </div>
    ),
  },
  {
    id: 'instante',
    title: 'Instanțe de procesare',
    content: (
      <div className="space-y-3 text-sm text-ink-2 leading-relaxed">
        <p>Clasificarea rulează pe o <strong>instanță</strong> pe care o porniți din <strong>Panou</strong>. Doar <strong>administratorii și proprietarii</strong> organizației (și operatorii platformei) pot porni sau opri o instanță — pornirea consumă credite reale. Membrii obișnuiți văd, în locul butonului, un mesaj care îi îndrumă să contacteze un administrator.</p>
        <ul className="list-disc list-inside space-y-1 pl-2">
          <li>Costul se calculează pe oră, la tariful organizației.</li>
          <li>Fiecare sesiune se facturează cu <strong>minim 1 oră</strong>; peste acest minim, timpul se contorizează în pași de 0,1 oră (6 minute), rotunjiți în sus.</li>
          <li>Instanța se <strong>oprește automat după 30 de minute</strong> fără activitate de clasificare.</li>
          <li>
            Dacă soldul este insuficient și realimentarea automată este dezactivată, pornirea este
            refuzată — mesajul apare deasupra butonului, iar butonul <strong>Pornește instanța</strong>{' '}
            rămâne disponibil, așa că puteți încerca din nou după ce administratorul alimentează
            portofelul.
          </li>
          <li>
            Dacă pornirea sau oprirea eșuează dintr-o eroare tehnică (nu din lipsă de sold, nici din
            capacitate indisponibilă), apare un mesaj de eroare sub buton — reîncercați apăsând din nou{' '}
            <strong>Pornește instanța</strong> respectiv <strong>Oprește instanța</strong>.
          </li>
        </ul>
        <h4 className="font-semibold text-ink pt-2">Pornirea instanței</h4>
        <p>
          Cât timp <strong>Panou</strong> arată <em>Nicio instanță activă</em>, tariful afișat lângă
          butonul <strong>Pornește instanța</strong> este o <strong>estimare</strong> (marcată cu „~"),
          folosită doar ca reper înainte de pornire.
        </p>
        <p>
          După apăsarea butonului, pornirea unei instanțe poate dura <strong>2–5 minute</strong> — cât
          timp durează, cardul afișează <em>Se pornește instanța…</em> Odată gata, cardul arată{' '}
          <em>Instanță activă</em> și tariful real, iar costul afișat pe card începe să crească pe măsură
          ce trece timpul.
        </p>
        <Callout type="info">
          Nu trebuie să așteptați ca instanța să devină activă pentru a continua lucrul: puteți încărca
          fișiere și porni mapări noi în tot acest interval — vezi secțiunea „Mapări" pentru cum se
          comportă o mapare pornită în timp ce instanța încă pornește.
        </Callout>
        <h4 className="font-semibold text-ink pt-2">Cardul de stare și bara de sus</h4>
        <p>
          Cardul sesiunii stă în colțul din dreapta-jos și nu blochează niciodată ecranul. Îl puteți
          muta în <strong>bara de sus</strong> (linia care deschide fiecare pagină, lângă indicatorul
          de stare a sistemului) apăsând săgeata <strong>⌃</strong> din capul cardului. Acolo rămâne ca
          o pastilă scurtă — <em>Instanță activă</em> sau <em>Se pornește</em>, plus timpul scurs —
          vizibilă pe orice pagină.
        </p>
        <p>
          Dacă îl ignorați și continuați să lucrați în pagină, cardul <strong>se mută singur</strong> în
          bara de sus după câteva secunde, ca să nu vă stea în cale. Apăsați pastila oricând pentru a
          readuce cardul complet — de acolo se oprește instanța. Pastila în sine este doar informativă:
          o apăsare greșită pe ea nu poate opri o instanță plătită.
        </p>
      </div>
    ),
  },
  {
    id: 'utilizare',
    title: 'Utilizare',
    content: (
      <div className="space-y-3 text-sm text-ink-2 leading-relaxed">
        <p>Pagina <strong>Utilizare</strong> arată soldul, <strong>Registrul de credite</strong> (toate alimentările și consumurile, cu soldul curent), lista <strong>Sesiunilor de instanță</strong> (început, sfârșit, stare, durată facturată, cost), tabelul <strong>Cost per mapare</strong> și un grafic cu <strong>consumul din ultimele 30 de zile</strong>.</p>
        <p>Datele din registru și din lista sesiunilor includ și ora (de ex. „13 iul. 2026, 12:00"), nu doar ziua, ca să se poată distinge mai multe mișcări din aceeași zi.</p>
        <p>Dacă soldul nu poate fi încărcat chiar atunci, coloana <strong>Sold curent</strong> din registru arată „—" în loc de o valoare calculată greșit.</p>
        <p>Durata facturată este afișată corect (de ex. „1,1 h"), reflectând minimul de 1 oră și pașii de 0,1 oră.</p>
        <p>
          Tabelul <strong>Cost per mapare</strong> listează mapările recent încheiate cu durata și
          costul lor de calcul (durată × tarif) — vezi „Costul unei mapări" din secțiunea Mapări
          pentru explicația detaliată, inclusiv diferența față de suma reținută efectiv din portofel.
        </p>
      </div>
    ),
  },
  {
    id: 'echipa',
    title: 'Echipă',
    content: (
      <div className="space-y-3 text-sm text-ink-2 leading-relaxed">
        <p>
          Secțiunea <strong>Echipă</strong> afișează lista membrilor organizației — nume, email și
          rol (<strong>Proprietar</strong>, <strong>Administrator</strong> sau <strong>Utilizator</strong>).
          Tabelul este vizibil oricărui membru autentificat; doar proprietarii și administratorii
          au acces la acțiunile de mai jos.
        </p>
        <h4 className="font-semibold text-ink pt-1">Drept de editare a nomenclatorului</h4>
        <p>
          Proprietarii și administratorii au implicit dreptul de a edita nomenclatorul (coloana
          Acțiuni arată „inclus în rol" pe rândul lor, fără casetă de bifat). Pentru un membru cu
          rolul <strong>Utilizator</strong>, un administrator poate acorda sau retrage explicit acest
          drept bifând sau debifând caseta <strong>Editare nomenclator</strong> de pe rândul lui —
          efectul se vede imediat în secțiunea Nomenclator.
        </p>
        <h4 className="font-semibold text-ink pt-1">Eliminarea unui membru</h4>
        <p>
          Butonul <strong>Elimină</strong>, cu confirmare directă în rând, scoate un membru din
          organizație. Nu este disponibil pe propriul rând (nu vă puteți elimina singur) și nici pe
          rândul unui proprietar dacă acesta este singurul proprietar al organizației — sistemul nu
          permite ca o organizație să rămână fără niciun proprietar.
        </p>
        <h4 className="font-semibold text-ink pt-1">Invitații</h4>
        <p>
          Sub tabelul de membri, proprietarii și administratorii au un formular de{' '}
          <strong>invitație</strong>: introduceți emailul (rolul este fix „Utilizator") și apăsați{' '}
          <strong>Invită</strong>. La succes apare linkul de invitație, gata de copiat și trimis
          persoanei respective — link-ul nu mai este afișat ulterior, în afara „Retrimite" (mai jos).
        </p>
        <Callout type="warn">
          Dacă emailul aparține deja unui membru sau are o invitație în așteptare, sistemul respinge
          cererea cu mesajul „Există deja un membru sau o invitație pentru acest email."
        </Callout>
        <p>
          Lista <strong>invitațiilor în așteptare</strong> arată emailul și data de expirare a
          fiecăreia. <strong>Retrimite</strong> prelungește valabilitatea și afișează un link nou;{' '}
          <strong>Anulează</strong> (cu confirmare directă) o retrage definitiv.
        </p>
      </div>
    ),
  },
  {
    id: 'setari',
    title: 'Setări',
    content: (
      <div className="space-y-3 text-sm text-ink-2 leading-relaxed">
        <p>
          Pagina <strong>Setări</strong> (accesibilă din pictograma de roată dințată din bara
          laterală) este vizibilă oricărui membru autentificat și conține profilul propriu,
          preferința de temă și datele organizației, în regim de citire.
        </p>
        <h4 className="font-semibold text-ink pt-1">Profil și schimbarea parolei</h4>
        <p>
          Secțiunea <strong>Profil</strong> arată numele și emailul contului curent. Formularul{' '}
          <strong>Schimbă parola</strong> cere parola curentă, parola nouă (minimum 8 caractere) și
          confirmarea ei; la succes apare mesajul „Parolă schimbată." și câmpurile se golesc. Dacă
          parola curentă este greșită sau parola nouă nu respectă cerințele, apare mesajul „Schimbarea
          parolei a eșuat. Verificați parola curentă."
        </p>
        <h4 className="font-semibold text-ink pt-1">Temă</h4>
        <p>
          Secțiunea <strong>Preferințe</strong> oferă o alegere cu trei opțiuni pentru aspectul
          aplicației: <strong>Luminoasă</strong>, <strong>Întunecată</strong> sau <strong>Sistem</strong>{' '}
          (urmărește automat preferința sistemului de operare cât timp pagina Setări este deschisă).
          Preferința rămâne salvată la următoarea vizită. Comutatorul rapid din bara laterală
          (pictograma soare/lună) rămâne o simplă alternare luminoasă/întunecată — alegerea „Sistem"
          se face doar din această pagină.
        </p>
        <h4 className="font-semibold text-ink pt-1">Organizație</h4>
        <p>
          Secțiunea <strong>Organizație</strong> arată, doar pentru informare, numele organizației,
          rolul dumneavoastră (<strong>Proprietar</strong>, <strong>Administrator</strong> sau{' '}
          <strong>Utilizator</strong>), planul pilot și starea realimentării automate a portofelului.
          Proprietarii și administratorii mai văd o notă „Plafon de depășire — în curând.", pentru o
          funcție de configurare a plafonului care nu este încă disponibilă.
        </p>
        <h4 className="font-semibold text-ink pt-1">Deconectare</h4>
        <p>
          Butonul <strong>Deconectare</strong> din josul paginii încheie sesiunea curentă și revine la
          ecranul de autentificare — identic cu opțiunea „Deconectare" din meniul de cont al barei
          laterale.
        </p>
      </div>
    ),
  },
  {
    id: 'operator',
    title: 'Consolă operator',
    content: (
      <div className="space-y-3 text-sm text-ink-2 leading-relaxed">
        <Callout type="info">
          Această secțiune este vizibilă doar <strong>operatorului platformei</strong> (administratorul
          PrivaMed care gestionează toate organizațiile) — pictograma <strong>Operator</strong> din
          bara laterală apare exclusiv contului cu acest rol special, distinct de rolurile
          Proprietar/Administrator/Utilizator dintr-o organizație.
        </Callout>
        <p>
          Pagina <strong>Operator → Organizații</strong> listează organizațiile din platformă, cu
          numărul de membri, <strong>Sold</strong>-ul portofelului de credite și starea{' '}
          <strong>Instanță</strong> (activă sau oprită) a fiecăreia — o privire de ansamblu peste toți
          clienții, nu doar organizația proprie.
        </p>
        <p>
          Puteți căuta liber după <strong>nume</strong> sau <strong>slug</strong>; căutarea se aplică
          la apăsarea <strong>Caută</strong> (sau Enter), nu la fiecare literă tastată.{' '}
          <strong>Resetează căutarea</strong> golește căutarea curentă. Lista este paginată, cu
          alegerea a 25, 50 sau 100 de organizații pe pagină și navigare înainte/înapoi — util atunci
          când platforma găzduiește mii de organizații.
        </p>
        <h4 className="font-semibold text-ink pt-1">Crearea unei organizații noi</h4>
        <p>
          Din formularul de deasupra listei, completați numele organizației, un <strong>slug</strong>{' '}
          unic (litere mici, cifre și cratime) și emailul administratorului ei, apoi apăsați{' '}
          <strong>Creează organizație</strong>. Organizația este creată imediat, iar sistemul afișează
          un <strong>link de activare</strong> pentru administratorul nou — copiați-l și trimiteți-l
          acelei persoane. La deschiderea linkului, administratorul își stabilește singur parola
          contului; nu există o parolă generată de sistem și nici autoînregistrare separată.
        </p>
        <Callout type="warn">
          Dacă slug-ul este deja folosit sau emailul aparține deja unui cont, cererea este respinsă cu
          un mesaj explicit — alegeți un alt slug sau verificați emailul administratorului.
        </Callout>
        <h4 className="font-semibold text-ink pt-1">Acordarea creditului</h4>
        <p>
          Pe rândul organizației dorite, introduceți suma în euro în câmpul din coloana{' '}
          <strong>Acțiuni</strong> și apăsați <strong>Acordă</strong> pentru a alimenta imediat
          portofelul ei — util, de exemplu, pentru a realimenta o organizație pilot al cărei sold s-a
          epuizat. Suma se adaugă la soldul curent, iar coloana <strong>Sold</strong> se actualizează
          îndată ce operația reușește.
        </p>
      </div>
    ),
  },
];

export function DocsView() {
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState(sections[0].id);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter((s) => s.title.toLowerCase().includes(q));
  }, [query]);

  // Scroll-spy: highlight the section nearest the top of the viewport. The
  // page (not this component) owns the scroll container — `AppShell`'s
  // content column — so this observes against the default (viewport) root
  // rather than a local ref; intersection is still correctly clipped through
  // that ancestor's own overflow.
  useEffect(() => {
    const els = filtered
      .map((s) => document.getElementById(`section-${s.id}`))
      .filter((el): el is HTMLElement => el != null);
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id.replace('section-', ''));
      },
      { rootMargin: '0px 0px -70% 0px', threshold: 0 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [filtered]);

  function jumpTo(id: string) {
    document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  }

  return (
    <div className="mt-8">
      <h1 className="text-[32px] font-[650] text-ink">Ghid</h1>
      <div className="mt-1 border-b-2 border-ink pb-3" />
      <p className="mt-3 max-w-prose text-[13.5px] text-ink-3">
        Manual de utilizare pentru operatori — de la autentificare, la pornirea unei mapări,
        revizuirea rezultatelor și export.
      </p>

      {/* Mobile navigation — the sticky aside below is lg-only, so without this
          a phone/tablet gets one long unnavigable column with no search. This
          collapsible gives the same jump list + search below lg. */}
      <details className="mt-6 rounded-md border border-rule-soft lg:hidden">
        <summary className="cursor-pointer px-4 py-2.5 text-[13.5px] font-semibold text-ink">
          Cuprins și căutare
        </summary>
        <div className="border-t border-rule-soft px-4 pb-4 pt-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Caută în manual…"
              aria-label="Caută în manual"
              className="h-8 w-full rounded-md border border-rule-soft bg-wash pl-9 pr-3 text-[13.5px] text-ink placeholder:text-ink-3"
            />
          </div>
          <nav className="mt-3">
            {filtered.length === 0 ? (
              <p className="px-1 py-3 text-[13.5px] text-ink-3">Niciun rezultat.</p>
            ) : (
              <ol className="space-y-0.5">
                {filtered.map((s) => {
                  const { num, text } = splitTitle(s.title);
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={(e) => {
                          jumpTo(s.id);
                          (e.currentTarget.closest('details') as HTMLDetailsElement | null)?.removeAttribute('open');
                        }}
                        className="group flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13.5px] text-ink-2 hover:bg-wash"
                      >
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-wash text-[11.5px] font-semibold tabular-nums text-ink-3 group-hover:bg-rule-soft">
                          {num ?? '•'}
                        </span>
                        <span className="truncate">{text}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </nav>
        </div>
      </details>

      <div className="mt-8 lg:grid lg:grid-cols-[220px_1fr] lg:items-start lg:gap-12">
        {/* Sidebar — sticky table of contents with scroll-spy + search */}
        <aside className="hidden lg:sticky lg:top-6 lg:flex lg:max-h-[calc(100vh-3rem)] lg:flex-col">
          <div className="flex items-center gap-2 text-ink">
            <BookOpen className="w-4 h-4 text-teal" />
            <span className="text-[13.5px] font-semibold">Manual</span>
          </div>
          <div className="relative mt-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Caută în manual…"
              aria-label="Caută în manual"
              className="h-8 w-full rounded-md border border-rule-soft bg-wash pl-9 pr-3 text-[13.5px] text-ink placeholder:text-ink-3"
            />
          </div>
          <nav className="mt-3 flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-1 py-3 text-[13.5px] text-ink-3">Niciun rezultat.</p>
            ) : (
              <ol className="space-y-0.5">
                {filtered.map((s) => {
                  const { num, text } = splitTitle(s.title);
                  const isActive = activeId === s.id;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => jumpTo(s.id)}
                        aria-current={isActive ? 'true' : undefined}
                        className={`group flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13.5px] ${
                          isActive ? 'bg-teal-wash text-teal font-medium' : 'text-ink-2 hover:bg-wash'
                        }`}
                      >
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11.5px] font-semibold tabular-nums ${
                            isActive ? 'bg-teal text-teal-ink' : 'bg-wash text-ink-3 group-hover:bg-rule-soft'
                          }`}
                        >
                          {num ?? '•'}
                        </span>
                        <span className="truncate">{text}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </nav>
        </aside>

        {/* Content pane. max-w caps the READING measure: the manual is a reading
            surface, so it keeps a ~72ch line even though the shared document
            canvas is now 1440px (which suits the tabular pages but would run this
            prose to ~150 chars/line). Capped here, not in PageCanvas, so the
            canvas contract stays simple. */}
        <div className="min-w-0 max-w-[72ch] space-y-14">
          {filtered.map((s) => {
            const { num, text } = splitTitle(s.title);
            return (
              <section key={s.id} id={`section-${s.id}`} className="scroll-mt-6">
                <div className="flex items-center gap-3 mb-4 pb-2 border-b border-rule-soft">
                  {num && (
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-teal-wash text-teal text-[13.5px] font-bold tabular-nums">
                      {num}
                    </span>
                  )}
                  <h2 className="text-lg font-bold tracking-tight text-ink">{text}</h2>
                </div>
                {s.content}
              </section>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-sm text-ink-3">
              Niciun rezultat pentru „{query}". Încercați alt termen.
            </p>
          )}
        </div>
      </div>

      <footer className="mt-16 border-t border-rule pt-6 text-center text-xs text-ink-3">
        PrivaMed Nomenclator — documentație internă
      </footer>
    </div>
  );
}
